<template>
  <div class="emp-settings-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <button class="back-btn" @click="goBack">
        <v-icon size="24" color="white">mdi-arrow-left</v-icon>
      </button>
      <div class="header-title">설정</div>
      <div class="header-spacer"></div>
    </div>

    <div class="page-content">
    
    <div class="settings-card">
      <div class="settings-item" @click="openPasswordDialog">
        <div class="item-left">
          <v-icon color="#059669" size="24">mdi-lock-reset</v-icon>
          <span>비밀번호 변경</span>
        </div>
        <v-icon color="#9ca3af" size="20">mdi-chevron-right</v-icon>
      </div>
      
      <div class="settings-item" @click="handleNotification">
        <div class="item-left">
          <v-icon color="#059669" size="24">mdi-bell-outline</v-icon>
          <span>알림 설정</span>
        </div>
        <v-icon color="#9ca3af" size="20">mdi-chevron-right</v-icon>
      </div>
      
      <div class="settings-item logout" @click="handleLogout">
        <div class="item-left">
          <v-icon color="#dc2626" size="24">mdi-logout</v-icon>
          <span>로그아웃</span>
        </div>
      </div>
    </div>
    
    <div class="version-info">
      <span>버전 1.0.0</span>
    </div>
    </div>

    <!-- 비밀번호 변경 다이얼로그 -->
    <v-dialog v-model="showPasswordDialog" max-width="360" >
      <v-card class="password-dialog-card">
        <div class="dialog-header">
          <h2>비밀번호 변경</h2>
          <button class="close-btn" @click="closePasswordDialog">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="dialog-content">
          <div class="input-group">
            <label>현재 비밀번호</label>
            <div class="password-input">
              <input
                :type="showCurrentPw ? 'text' : 'password'"
                v-model="passwordForm.currentPw"
                placeholder="현재 비밀번호 입력"
              />
              <button 
                type="button" 
                class="toggle-btn"
                @click="showCurrentPw = !showCurrentPw"
              >
                <v-icon size="20" color="#9ca3af">
                  {{ showCurrentPw ? 'mdi-eye-off' : 'mdi-eye' }}
                </v-icon>
              </button>
            </div>
          </div>

          <div class="input-group">
            <label>새 비밀번호</label>
            <div class="password-input">
              <input
                :type="showNewPw ? 'text' : 'password'"
                v-model="passwordForm.newPw"
                placeholder="새 비밀번호 입력 (4자 이상)"
              />
              <button 
                type="button" 
                class="toggle-btn"
                @click="showNewPw = !showNewPw"
              >
                <v-icon size="20" color="#9ca3af">
                  {{ showNewPw ? 'mdi-eye-off' : 'mdi-eye' }}
                </v-icon>
              </button>
            </div>
            <span class="input-hint" v-if="passwordForm.newPw && passwordForm.newPw.length < 4">
              비밀번호는 4자 이상이어야 합니다.
            </span>
          </div>

          <div class="input-group">
            <label>새 비밀번호 확인</label>
            <div class="password-input">
              <input
                :type="showConfirmPw ? 'text' : 'password'"
                v-model="passwordForm.confirmPw"
                placeholder="새 비밀번호 다시 입력"
              />
              <button 
                type="button" 
                class="toggle-btn"
                @click="showConfirmPw = !showConfirmPw"
              >
                <v-icon size="20" color="#9ca3af">
                  {{ showConfirmPw ? 'mdi-eye-off' : 'mdi-eye' }}
                </v-icon>
              </button>
            </div>
            <span 
              class="input-hint error" 
              v-if="passwordForm.confirmPw && passwordForm.newPw !== passwordForm.confirmPw"
            >
              비밀번호가 일치하지 않습니다.
            </span>
            <span 
              class="input-hint success" 
              v-else-if="passwordForm.confirmPw && passwordForm.newPw === passwordForm.confirmPw"
            >
              비밀번호가 일치합니다.
            </span>
          </div>
        </div>

        <div class="dialog-actions">
          <button class="cancel-btn" @click="closePasswordDialog">취소</button>
          <button 
            class="submit-btn" 
            :disabled="!isPasswordFormValid || isChangingPassword"
            @click="changePassword"
          >
            <v-progress-circular 
              v-if="isChangingPassword" 
              indeterminate 
              size="18" 
              width="2"
              color="white"
            />
            <span v-else>변경하기</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 로그아웃 확인 다이얼로그 -->
    <v-dialog v-model="showLogoutDialog" max-width="300">
      <v-card class="logout-dialog-card">
        <v-card-title class="dialog-title">로그아웃</v-card-title>
        <v-card-text class="dialog-text">
          정말 로그아웃 하시겠습니까?
        </v-card-text>
        <v-card-actions class="dialog-btns">
          <v-btn variant="text" @click="showLogoutDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" @click="confirmLogout">로그아웃</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();

// 뒤로가기
const goBack = () => {
  router.back();
};

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);

// 비밀번호 변경 상태
const showPasswordDialog = ref(false);
const showCurrentPw = ref(false);
const showNewPw = ref(false);
const showConfirmPw = ref(false);
const isChangingPassword = ref(false);
const passwordForm = ref({
  currentPw: '',
  newPw: '',
  confirmPw: ''
});

// 로그아웃 다이얼로그
const showLogoutDialog = ref(false);

// 폼 유효성 검사
const isPasswordFormValid = computed(() => {
  return (
    passwordForm.value.currentPw.length > 0 &&
    passwordForm.value.newPw.length >= 4 &&
    passwordForm.value.newPw === passwordForm.value.confirmPw
  );
});

// 비밀번호 다이얼로그 열기
const openPasswordDialog = () => {
  passwordForm.value = { currentPw: '', newPw: '', confirmPw: '' };
  showCurrentPw.value = false;
  showNewPw.value = false;
  showConfirmPw.value = false;
  showPasswordDialog.value = true;
};

// 비밀번호 다이얼로그 닫기
const closePasswordDialog = () => {
  showPasswordDialog.value = false;
  passwordForm.value = { currentPw: '', newPw: '', confirmPw: '' };
};

// 비밀번호 변경
const changePassword = async () => {
  if (!isPasswordFormValid.value) return;
  if (!pwaUser.value?.emp_idx) {
    store.commit('showSnackbar', {
      text: '사원 정보를 찾을 수 없습니다.',
      color: 'error'
    });
    return;
  }

  isChangingPassword.value = true;
  try {
    const response = await axios.put(
      `/api/emp/facility/${pwaUser.value.emp_idx}/password`,
      {
        currentPw: passwordForm.value.currentPw,
        newPw: passwordForm.value.newPw
      },
      { withCredentials: true }
    );

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: '비밀번호가 성공적으로 변경되었습니다.',
        color: 'success'
      });
      closePasswordDialog();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '비밀번호 변경에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('비밀번호 변경 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isChangingPassword.value = false;
  }
};

const handleNotification = () => {
  store.commit('showSnackbar', {
    text: '알림 설정 기능이 곧 제공됩니다.',
    color: 'info'
  });
};

const handleLogout = () => {
  showLogoutDialog.value = true;
};

const confirmLogout = async () => {
  showLogoutDialog.value = false;
  await store.dispatch('pwa/performPwaLogout');
  router.push('/pwa/login');
};
</script>

<style scoped lang="scss">
.emp-settings-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
  }
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
  
  .header-spacer {
    width: 32px;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.settings-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.settings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:active {
    background-color: #f9fafb;
  }
  
  .item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    
    span {
      font-size: 15px;
      font-weight: 500;
      color: #1f2937;
    }
  }
  
  &.logout {
    .item-left span {
      color: #dc2626;
    }
  }
}

.version-info {
  text-align: center;
  margin-top: 32px;
  
  span {
    font-size: 13px;
    color: #9ca3af;
  }
}

// 비밀번호 변경 다이얼로그
.password-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  
  h2 {
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f3f4f6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

.dialog-content {
  padding: 20px;
}

.input-group {
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }
}

.password-input {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s ease;
  
  &:focus-within {
    border-color: #059669;
  }
  
  input {
    flex: 1;
    padding: 12px 14px;
    font-size: 15px;
    border: none;
    outline: none;
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .toggle-btn {
    padding: 0 12px;
    border: none;
    background: transparent;
    cursor: pointer;
  }
}

.input-hint {
  display: block;
  font-size: 12px;
  margin-top: 4px;
  color: #6b7280;
  
  &.error {
    color: #dc2626;
  }
  
  &.success {
    color: #059669;
  }
}

.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f3f4f6;
}

.cancel-btn {
  flex: 1;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:active {
    background: #e5e7eb;
  }
}

.submit-btn {
  flex: 1;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
  
  &:not(:disabled):active {
    transform: scale(0.98);
  }
}

// 로그아웃 다이얼로그
.logout-dialog-card {
  border-radius: 16px !important;
}

.dialog-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #1f2937;
}

.dialog-text {
  font-size: 15px;
  color: #6b7280;
}

.dialog-btns {
  padding: 8px 16px 16px !important;
  justify-content: flex-end;
}
</style>
